import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['swatch', 'hex', 'rgb', 'themeSelect', 'themeWrapper']

  declare readonly swatchTargets: HTMLElement[]
  declare readonly hexTargets: HTMLElement[]
  declare readonly rgbTargets: HTMLElement[]
  declare readonly themeSelectTarget: HTMLSelectElement
  declare readonly themeWrapperTarget: HTMLDivElement
  declare readonly hasHexTarget: boolean
  declare readonly hasRgbTarget: boolean
  declare readonly hasThemeSelectTarget: boolean
  declare readonly hasThemeWrapperTarget: boolean

  connect (): void {
    if (this.hasThemeWrapperTarget && this.hasThemeSelectTarget) {
      const currentTheme = this.themeWrapperTarget.getAttribute('data-theme')
      if (currentTheme != null && currentTheme !== '') {
        this.themeSelectTarget.value = currentTheme
      }
    }

    this.updateColors()
  }

  updateColors (): void {
    this.swatchTargets.forEach((el, index) => {
      const style = getComputedStyle(el)
      const rgb = style.backgroundColor
      if (rgb == null || rgb === '') return

      const hex = this.rgbToHex(rgb)
      const rgba = this.rgbToRgba(rgb)

      if (this.hasHexTarget && this.hexTargets[index] != null) {
        this.hexTargets[index].textContent = hex
      }
      if (this.hasRgbTarget && this.rgbTargets[index] != null) {
        this.rgbTargets[index].textContent = rgba
      }
    })
  }

  rgbToHex (rgb: string): string {
    const modernMatch = rgb.match(/rgb\(([^)]+)\)/)
    if (modernMatch !== null && rgb.includes('/')) {
      const [r, g, b] = modernMatch[1].split('/')[0].trim().split(/\s+/).map(Number)
      return this.formatHex(r, g, b)
    }

    const classicMatch = rgb.match(/\d+/g)
    if (classicMatch == null) return ''
    const [r, g, b] = classicMatch.map(Number)
    return this.formatHex(r, g, b)
  }

  rgbToRgba (rgb: string): string {
    const modernMatch = rgb.match(/rgb\(([^)]+)\)/)
    if (modernMatch !== null && rgb.includes('/')) {
      const [rgbPart, alphaPart] = modernMatch[1].split('/')
      const [r, g, b] = rgbPart.trim().split(/\s+/).map(Number)
      const a = parseFloat(alphaPart)
      return `rgba(${r}, ${g}, ${b}, ${a})`
    }

    const classicMatch = rgb.match(/\d+(\.\d+)?/g)
    if (classicMatch == null) return ''
    const [r, g, b, a = '1'] = classicMatch
    return `rgba(${r}, ${g}, ${b}, ${a})`
  }

  formatHex (r: number, g: number, b: number): string {
    const clamp = (n: number): number => Math.max(0, Math.min(255, n))
    return (
      '#' +
      [r, g, b].map((x) => clamp(x).toString(16).padStart(2, '0')).join('').toUpperCase()
    )
  }

  changeTheme (event: Event): void {
    if (!this.hasThemeWrapperTarget) return

    const target = event.target as HTMLSelectElement
    const selectedTheme = target.value
    this.themeWrapperTarget.setAttribute('data-theme', selectedTheme)
    requestAnimationFrame(() => this.updateColors())
  }
}
